import { Flex } from '@chakra-ui/react';
import { EmailContentForm } from 'src/components/EmailModule';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Overlay } from 'src/components/Overlay';
import { Button, useToast } from '@morressier/alembic';
import { useSendAdhocEmail } from 'src/api/commands';
import ComposeReviewerInvitation from '../ReviewTab/InviteReviewer/ComposeReviewerInvitation';
import { AdHocModalOpen } from './ComposeEmailModal';
import { normalizeRecipients } from '../../../utils/normalizeEmailRecipients';
import { zodResolver } from '@hookform/resolvers/zod';
import { emailContentSchema } from '@publishing-engine/domain/validation';
import { getAllTemplatesByCommand } from '../../../api/email-service';
import { useQuery } from '@tanstack/react-query';

interface Props {
  setOpenSendEmailModal: (data: AdHocModalOpen) => void;
  organizationId: string;
  email: string[];
}

const UserComposeEmailModal = ({
  setOpenSendEmailModal,
  organizationId,
  email
}: Props) => {
  const toast = useToast();
  const emailContentMethods = useForm<EmailContentForm>({
    defaultValues: {
      emailContent: {
        body: '',
        subject: '',
        recipients: { to: [{ text: email.join(',') }], cc: [], bcc: [] },
        attachments: []
      }
    },
    resolver: zodResolver(emailContentSchema)
  });

  const { data: emailTemplates, isLoading: isLoadingTemplates } = useQuery({
    queryKey: ['browse-email-templates', organizationId, 'SendAdhocEmail'],
    queryFn: () =>
      organizationId
        ? getAllTemplatesByCommand({
            organizationId,
            commandName: 'SendAdhocEmail'
          })
        : null,
    enabled: !!organizationId
  });

  const testEmailSendKey = ['send-adhoc-email', email];

  const sendAdhocEmailMutation = useSendAdhocEmail(
    organizationId,
    testEmailSendKey
  );
  const { reset, handleSubmit } = emailContentMethods;
  const onSubmit: SubmitHandler<EmailContentForm> = async data => {
    const { emailContent } = data;
    const emailPayload = {
      ...emailContent,
      recipients: normalizeRecipients(emailContent.recipients),
      attachments: emailContent.attachments || []
    };
    try {
      await sendAdhocEmailMutation.mutateAsync({
        email: emailPayload
      });
      toast({
        variant: 'success',
        title: 'Email successfully sent.',
        status: 'success'
      });
      setOpenSendEmailModal({
        status: false,
        defaultEmail: []
      });
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Failed to send the email.',
        status: 'error'
      });
    }
  };
  return (
    <>
      <FormProvider {...emailContentMethods}>
        <form>
          <Overlay>
            <ComposeReviewerInvitation
              currentStep={0}
              totalSteps={0}
              emails={email}
              emailTemplates={emailTemplates?.data}
              isLoadingTemplates={isLoadingTemplates}
              attachments={[]}
              footer={
                <Flex width="100%" justifyContent="space-between">
                  <Button
                    variant="caution"
                    onClick={() => {
                      setOpenSendEmailModal({
                        status: false,
                        defaultEmail: []
                      });
                      reset();
                    }}
                  >
                    Exit
                  </Button>

                  <Flex gap={4}>
                    <Button
                      variant="primary"
                      type="submit"
                      onClick={handleSubmit(onSubmit)}
                    >
                      Send Email
                    </Button>
                  </Flex>
                </Flex>
              }
              adHocEmail={true}
              removeBrowseTemplates={true}
              commandName="SendAdhocEmail"
            />
          </Overlay>
        </form>
      </FormProvider>
    </>
  );
};

export default UserComposeEmailModal;
